/**
 * Color Picker Component
 * Handles theme color selection with preset options and custom color picker
 */

import React, { useState, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Palette, Check, AlertCircle } from 'lucide-react';
import { validateHexColor, getContrastingTextColor } from '../../utils/formatters';
import { THEME_COLOR_PRESETS } from '../../services/business/types';

interface ColorPickerComponentProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  error?: string;
}

export const ColorPickerComponent: React.FC<ColorPickerComponentProps> = ({
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const [customColor, setCustomColor] = useState(value);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handlePresetSelect = useCallback(
    (color: string) => {
      setCustomColor(color);
      setValidationError(null);
      onChange(color);
    },
    [onChange]
  );

  const handleCustomColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newColor = event.target.value;
      setCustomColor(newColor);

      // Validate the color
      if (newColor && !validateHexColor(newColor)) {
        setValidationError('Please enter a valid hex color (e.g., #3b82f6)');
        return;
      }

      setValidationError(null);
      if (newColor && validateHexColor(newColor)) {
        onChange(newColor);
      }
    },
    [onChange]
  );

  // Hidden color input ref for native color picker
  const hiddenColorInputRef = useRef<HTMLInputElement>(null);

  const handleColorInputClick = useCallback(() => {
    hiddenColorInputRef.current?.click();
  }, []);

  const handleHiddenColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newColor = event.target.value;
      setCustomColor(newColor);
      setValidationError(null);
      onChange(newColor);
    },
    [onChange]
  );

  const displayError = error || validationError;
  const isValidColor = validateHexColor(value);

  return (
    <div className="space-y-4">
      <Label>Theme Color</Label>
      <p className="text-sm text-muted-foreground">
        Choose a color theme for your workspace interface
      </p>

      {/* Color Preview */}
      <div className="flex items-center space-x-3">
        <div
          className="w-12 h-12 rounded-lg border-2 border-gray-200 flex items-center justify-center"
          style={{ backgroundColor: isValidColor ? value : '#f3f4f6' }}
        >
          {isValidColor && (
            <Check 
              className="h-6 w-6" 
              style={{ color: getContrastingTextColor(value) }}
            />
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">Current Color</p>
          <p className="text-xs text-muted-foreground">
            {isValidColor ? value.toUpperCase() : 'Invalid color'}
          </p>
        </div>
      </div>

      {/* Preset Colors */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Preset Colors</h4>
        <div className="grid grid-cols-6 gap-2">
          {THEME_COLOR_PRESETS.map((preset) => (
            <button
              key={preset.color}
              type="button"
              onClick={() => handlePresetSelect(preset.color)}
              disabled={disabled}
              className={`
                w-10 h-10 rounded-lg border-2 transition-all
                ${value === preset.color ? 'border-gray-900 scale-110' : 'border-gray-200 hover:border-gray-400'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
              style={{ backgroundColor: preset.color }}
              title={preset.name}
            >
              {value === preset.color && (
                <Check 
                  className="h-5 w-5 mx-auto" 
                  style={{ color: getContrastingTextColor(preset.color) }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Color Input */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">Custom Color</h4>
        <div className="flex space-x-2">
          <div className="flex-1">
            <Input
              type="text"
              value={customColor}
              onChange={handleCustomColorChange}
              placeholder="#3b82f6"
              disabled={disabled}
              className={displayError ? 'border-red-300' : ''}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleColorInputClick}
            disabled={disabled}
            title="Open color picker"
          >
            <Palette className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Helper text */}
        <p className="text-xs text-muted-foreground">
          Enter a hex color code (e.g., #3b82f6) or click the palette icon to use the color picker
        </p>
      </div>

      {/* Hidden color input for native color picker */}
      <input
        ref={hiddenColorInputRef}
        type="color"
        value={value}
        onChange={handleHiddenColorChange}
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
      />

      {/* Error Display */}
      {displayError && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span>{displayError}</span>
        </div>
      )}

      {/* Color Usage Examples */}
      {isValidColor && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Preview</h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: value }}
              />
              <span className="text-sm">Primary buttons and links</span>
            </div>
            <div className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded border"
                style={{ backgroundColor: `${value}20` }}
              />
              <span className="text-sm">Hover states and highlights</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPickerComponent;
